import { createTheme, ThemeOptions } from '@mui/material/styles';

// Base theme configuration - shared between light and dark modes
const baseThemeOptions: ThemeOptions = {
  palette: {
    primary: {
      main: '#33bbaa',
      light: '#5ecdc1',
      dark: '#289080',
    },
    secondary: {
      main: '#00bbff',
      light: '#33ccff',
      dark: '#0099cc',
    },
    success: {
      main: '#34C759',
    },
    error: {
      main: '#FF3B30',
    },
    warning: {
      main: '#FF9500',
    },
    info: {
      main: '#00bbff',
    },
    grey: {
      50: '#F8F9FA',
      100: '#F5F5F5',
      200: '#E8E8E8',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#666666',
      600: '#4b5563',
      700: '#374151',
      800: '#1C1C1E',
      900: '#111827',
    },
  },
  typography: {
    fontFamily: '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", sans-serif',
    h1: {
      fontFamily: '"Nunito", sans-serif',
      fontSize: 'clamp(2rem, 4vw, 2.75rem)',
      fontWeight: 800,
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    h2: {
      fontFamily: '"Nunito", sans-serif',
      fontSize: 'clamp(1.625rem, 3.25vw, 2.25rem)',
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: '-0.005em',
    },
    h3: {
      fontFamily: '"Nunito", sans-serif',
      fontSize: 'clamp(1.375rem, 2.75vw, 1.875rem)',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '0em',
    },
    h4: {
      fontFamily: '"Nunito", sans-serif',
      fontSize: 'clamp(1.25rem, 2.5vw, 1.625rem)',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '0em',
    },
    h5: {
      fontFamily: '"Nunito", sans-serif',
      fontSize: 'clamp(1.125rem, 2.25vw, 1.375rem)',
      fontWeight: 600,
      lineHeight: 1.45,
      letterSpacing: '0em',
    },
    h6: {
      fontFamily: '"Nunito", sans-serif',
      fontSize: 'clamp(1rem, 2vw, 1.25rem)',
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0em',
    },
    body1: {
      fontSize: 'clamp(1.125rem, 1.75vw, 1.25rem)',
      lineHeight: 1.75,
      fontWeight: 400,
      letterSpacing: '0em',
    },
    body2: {
      fontSize: 'clamp(1rem, 1.5vw, 1.125rem)',
      lineHeight: 1.7,
      fontWeight: 400,
      letterSpacing: '0em',
    },
    button: {
      fontFamily: '"Nunito", sans-serif',
      fontSize: 'clamp(1rem, 1.75vw, 1.125rem)',
      fontWeight: 700,
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '12px 24px',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)',
          border: '1px solid rgba(0,0,0,0.05)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 24,
          paddingRight: 24,
        },
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
};

// Light theme
const lightThemeOptions: ThemeOptions = {
  ...baseThemeOptions,
  palette: {
    ...baseThemeOptions.palette,
    mode: 'light',
    background: {
      default: '#FEFEFE',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1C1C1E',
      secondary: '#666666',
    },
    divider: 'rgba(0, 0, 0, 0.12)', // Standard divider for light mode
  },
};

// Dark theme
const darkThemeOptions: ThemeOptions = {
  ...baseThemeOptions,
  palette: {
    ...baseThemeOptions.palette,
    mode: 'dark',
    background: {
      default: '#0F172A', // Dark slate background
      paper: '#1E293B', // Darker slate for cards/paper
    },
    text: {
      primary: '#F1F5F9', // Light text for dark background
      secondary: '#CBD5E1', // Lighter secondary text
    },
    divider: 'rgba(255, 255, 255, 0.12)', // Subtle divider for dark mode
  },
};

// Create theme function that accepts mode
export const createAppTheme = (mode: 'light' | 'dark') => {
  return createTheme(mode === 'dark' ? darkThemeOptions : lightThemeOptions);
};

// Default export for backward compatibility (light theme)
export const theme = createTheme(lightThemeOptions);

